/**
 * The coordinator's talent list (part 3d, 18 Sep 2026): filters, sorting, distance, paging. Pure.
 * Rows come from public.coordinator_talent (one per talent). Distances are straight-line from a
 * chosen place to the talent's home, or to the nearest event they have worked, whichever is closer.
 */
import { haversineMiles, isCoord } from './dayPlan.js'

export const TALENT_SORTS = [
  { key: 'name', label: 'Name' },
  { key: 'last_message', label: 'Last message, newest' },
  { key: 'quietest', label: 'Longest since any message' },
  { key: 'completed', label: 'Most bookings completed' },
  { key: 'last_event', label: 'Most recent event' },
  { key: 'distance', label: 'Closest first' }
]
export const TALENT_ROLES = [
  { key: 'all', label: 'Photo and video' },
  { key: 'photo', label: 'Photographers' },
  { key: 'video', label: 'Videographers' }
]
export const TALENT_STANDINGS = [
  { key: 'good', label: 'Good standing' },
  { key: 'all', label: 'Everyone' },
  { key: 'black', label: 'Blacklisted only' }
]
export const QUIET_OPTIONS = [
  { key: 0, label: 'Any time' },
  { key: 30, label: 'No message in 30 days' },
  { key: 60, label: 'No message in 60 days' },
  { key: 90, label: 'No message in 90 days' },
  { key: -1, label: 'Never messaged' }
]
export const NEAR_RADII = [25, 50, 100, 150, 250]
export const PAGE_SIZE = 100

const round1 = (n) => Math.round(n * 10) / 10
const time = (iso) => { const t = new Date(iso || 0).getTime(); return Number.isFinite(t) ? t : 0 }

export function daysSince(iso, today) {
  if (!iso) return null
  const t = Date.parse(String(iso).slice(0, 10) + 'T00:00:00Z')
  const now = Date.parse(today + 'T00:00:00Z')
  return Number.isFinite(t) && Number.isFinite(now) ? Math.round((now - t) / 86400000) : null
}

export const isBlacklisted = (r) => r.status === 'Blacklisted' || r.is_blacklisted === true

/**
 * Miles from `center` to this talent: home coordinates or the nearest event they worked
 * (`provenByTalent`: Map talent_id → [{ lat, lng }]). Returns { miles, source } or null.
 */
export function nearDistance(row, center, provenByTalent = new Map()) {
  if (!center || !isCoord(center)) return null
  let best = null
  const home = haversineMiles(center, { lat: row.latitude, lng: row.longitude })
  if (home !== null) best = { miles: round1(home), source: 'home' }
  for (const pt of provenByTalent.get(row.talent_id) || []) {
    const d = haversineMiles(center, pt)
    if (d !== null && (!best || d < best.miles)) best = { miles: round1(d), source: 'worked' }
  }
  return best
}

/**
 * Apply the list filters. `near` = { center: {lat,lng}, radius } or null. When `near` is set every
 * row gains `distance` ({miles, source}) and rows without any coordinates drop out.
 */
export function filterTalent(rows, { q = '', role = 'all', standing = 'good', quietDays = 0, neverWorked = false, near = null, provenByTalent = new Map(), today } = {}) {
  const needle = String(q || '').trim().toLowerCase()
  const out = []
  for (const r0 of rows || []) {
    const r = { ...r0 }
    if (role === 'photo' && r.is_videographer) continue
    if (role === 'video' && !r.is_videographer) continue
    const black = isBlacklisted(r)
    if (standing === 'good' && black) continue
    if (standing === 'black' && !black) continue
    if (needle) {
      const hay = [r.full_name, r.email, r.city, r.state].map((v) => String(v || '').toLowerCase()).join(' ')
      if (!hay.includes(needle)) continue
    }
    if (neverWorked && (r.completed_count || 0) > 0) continue
    if (quietDays === -1 && r.last_message_at) continue
    if (quietDays > 0) {
      const d = daysSince(r.last_message_at, today)
      if (d !== null && d < quietDays) continue
    }
    if (near) {
      const dist = nearDistance(r, near.center, provenByTalent)
      if (!dist || dist.miles > near.radius) continue
      r.distance = dist
    }
    out.push(r)
  }
  return out
}

export function sortTalent(rows, sort = 'name') {
  const list = [...(rows || [])]
  const byName = (a, b) => String(a.full_name || '').localeCompare(String(b.full_name || ''))
  switch (sort) {
    case 'last_message': list.sort((a, b) => time(b.last_message_at) - time(a.last_message_at) || byName(a, b)); break
    case 'quietest': list.sort((a, b) => time(a.last_message_at) - time(b.last_message_at) || byName(a, b)); break
    case 'completed': list.sort((a, b) => (b.completed_count || 0) - (a.completed_count || 0) || byName(a, b)); break
    case 'last_event': list.sort((a, b) => time(b.last_event_date) - time(a.last_event_date) || byName(a, b)); break
    case 'distance': list.sort((a, b) => ((a.distance?.miles ?? Infinity) - (b.distance?.miles ?? Infinity)) || byName(a, b)); break
    default: list.sort(byName)
  }
  return list
}

export function pageTalent(rows, page = 1, per = PAGE_SIZE) {
  const total = (rows || []).length
  const pages = Math.max(1, Math.ceil(total / per))
  const p = Math.min(Math.max(1, Number(page) || 1), pages)
  return { total, page: p, pages, per, rows: (rows || []).slice((p - 1) * per, p * per) }
}

/** Bulk blacklist input check, mirrored on the server: a reason is required when blacklisting. */
export function validateBlacklist({ ids = [], reason = '', remove = false }) {
  if (!Array.isArray(ids) || ids.length === 0) return { ok: false, error: 'Pick at least one person' }
  if (ids.length > 50) return { ok: false, error: 'At most 50 at a time' }
  if (!remove && !String(reason || '').trim()) return { ok: false, error: 'A reason is required to blacklist' }
  return { ok: true }
}
